**Terms & Conditions**

**FieldOps Notes**

*Effective Date: June 1, 2026*

FieldOps Notes, Inc. d/b/a FieldOps Notes ("FieldOps Notes," "Company," "we," "our," or "us") provides the FieldOps Notes mobile application and the website at fieldopsai.team (together, the "Services") to authorized organizations and their canvassers for canvassing-session capture, field documentation, and management review. These Terms & Conditions (these "Terms") constitute a legal agreement between you and FieldOps Notes and govern your use of the Services. BY ACCESSING OR USING THE SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICES.

# **Table of Contents**

1\. Grant of License

2\. Eligibility

3\. User Accounts and Responsibilities

4\. Permitted Use

5\. Prohibited Conduct

6\. Intellectual Property Rights

7\. Microphone and Location Access

8\. Third-Party Links and Services

9\. Limitation of Liability

10\. Disclaimers

11\. Indemnification

12\. Termination

13\. Modification of Terms

14\. Governing Law and Dispute Resolution

15\. Severability

16\. Contact Us

# **1\. Grant of License**

Subject to your compliance with these Terms and our Privacy Policy, FieldOps Notes grants you a limited, non-exclusive, non-transferable, revocable license to use the Services for authorized canvassing activity, field documentation, and related organization purposes, either on your own behalf or on behalf of the organization that authorized your account. This license does not permit you to resell, sublicense, or commercialize the Services without FieldOps Notes' prior written consent.

# **2\. Eligibility**

You represent and warrant that:

●      you are at least 18 years of age;

●      you have the legal authority to enter into this agreement (and, if using the Services on behalf of an organization, that organization has authorized you to bind it to these Terms);

●      your use of the Services complies with all applicable laws, regulations, and industry standards in your jurisdiction; and

●      you will not use the Services for any illegal, fraudulent, or deceptive purpose.

# **3\. User Accounts and Responsibilities**

**Account Creation.**

To use certain features of the Services, you may be required to create an account. You agree to provide accurate, complete, and current information during registration and to keep that information updated.

**Account Security.**

You are responsible for maintaining the confidentiality of your login credentials and are liable for any unauthorized use of your account. You agree to notify FieldOps Notes immediately of any unauthorized access or security breach.

**Organization Accounts.**

If you register or use an account on behalf of an organization, you represent that you are authorized to do so. The organization is responsible for managing its authorized users and for its use of information made available through the Services, including canvassing-session, route, doorstep-interaction, field-note, and reporting information.

# **4\. Permitted Use**

You may use the Services solely for:

- conducting and documenting authorized canvassing activity;
- starting, recording, ending, and recovering canvassing sessions;
- capturing doorstep interactions, route information, field notes, and related visit documentation;
- reviewing organization-authorized dashboards, reports, visit records, and session diagnostics;
- using information made available by your organization only for authorized canvassing purposes; and
- any other purpose expressly permitted by these Terms or authorized in writing by FieldOps Notes.

# **5\. Prohibited Conduct**

You agree NOT to:

●      use the Services for any illegal, fraudulent, or deceptive purpose;

●      use the Services to harass, threaten, defame, or harm any person or organization;

●      attempt to gain unauthorized access to the Services, circumvent security measures, or probe for vulnerabilities;

●      reverse-engineer, decompile, or attempt to derive source code from the Services;

●      use the Services to distribute malware, viruses, or any other malicious software;

●      interfere with or disrupt the operation of the Services or its infrastructure;

●      use the Services to collect, store, or process personal information of others without consent;

●      use the Services in any manner that violates applicable laws, regulations, or industry standards;

●      attempt to access or download personal information of other users without authorization; or

●      use the Services to compete with FieldOps Notes or reverse-engineer competitive intelligence.

You agree not to falsify, spoof, manipulate, or misrepresent location, audio, session, visit, household, voter, or field-documentation information; use the Services to contact or profile individuals outside the authority granted by your organization or applicable law; or use information made available through the Services for an unauthorized purpose.

# **6\. Intellectual Property Rights**

The Services and all content, features, functionality, graphics, logos, brand names, designs, tools, and materials contained therein (the "Content") are owned by or licensed to FieldOps Notes and are protected by copyright, trademark, patent, and other intellectual property laws. You may not copy, modify, distribute, sell, lease, sublicense, or exploit any part of the Content without FieldOps Notes' prior written consent.

You retain any rights you hold in content you submit to the Services. By submitting content, you grant FieldOps Notes a worldwide, non-exclusive, royalty-free license to host, store, reproduce, process, transform, and display that content as reasonably necessary to operate, secure, support, and improve the Services, provide authorized organization functionality, and comply with legal obligations.

# **7\. Microphone and Location Access**

**Permissions.**

FieldOps Notes accesses your device microphone and precise location only after the permissions and disclosures required by the device platform and applicable law. Microphone and location capture begins as part of a user-initiated canvassing session and may continue while the application is in the background or the screen is off until the session is ended, recovered, or automatically ended under the application's session controls. You may revoke permissions through device settings, but doing so may prevent a session from starting or may interrupt or limit session capture.

**Lawful Recording and Canvassing.**

You and your organization are responsible for determining and complying with recording-consent, notice, canvassing, election, privacy, trespass, do-not-contact, and other laws applicable to each location and interaction where the Services are used.

**Organization Visibility.**

Session, route, location, audio, transcript, summary, observation, field-note, and related visit information may be available to authorized users of your organization, subject to your organization's policies and applicable law.

# **8\. Third-Party Links and Services**

The Services may use or link to third-party services, including mapping, hosting, storage, transcription, language-processing, communications, monitoring, and authentication services. Those services may be subject to separate terms and privacy policies. FieldOps Notes is not responsible for third-party services except to the extent required by applicable law or an agreement that expressly provides otherwise.

# **9\. Limitation of Liability**

TO THE FULLEST EXTENT PERMITTED BY LAW, FieldOps Notes SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES (INCLUDING LOST PROFITS, LOST REVENUE, LOST DATA, OR BUSINESS INTERRUPTION) ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES, EVEN IF FieldOps Notes HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

FieldOps Notes' TOTAL LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID FieldOps Notes IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR $100, WHICHEVER IS LESS.

SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY, SO THESE LIMITATIONS MAY NOT APPLY TO YOU.

# **10\. Disclaimers**

**AS-IS BASIS.**

THE SERVICES ARE PROVIDED ON AN "AS-IS" AND "AS-AVAILABLE" BASIS, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

**NO GUARANTEE OF AVAILABILITY.**

FieldOps Notes DOES NOT GUARANTEE THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS. INTERRUPTIONS, ERRORS, AND DATA LOSS MAY OCCUR.

**LOCATION AND AUDIO DATA.**

While we implement reasonable safeguards, FieldOps Notes does not warrant the absolute accuracy, reliability, or security of GPS location data or audio recordings. Network outages, device limitations, GPS signal loss, and other factors may affect data collection and accuracy.

# **11\. Indemnification**

You agree to indemnify, defend, and hold harmless FieldOps Notes, its officers, directors, employees, and agents from any claim, demand, loss, or damage (including reasonable legal fees) arising out of or related to:

●      your violation of these Terms;

●      your use of the Services;

●      your User Content;

●      your violation of any applicable law or regulation;

●      your infringement of any third-party intellectual property or privacy rights; or

●      any misuse of location data or falsification of job-site or time-worked information.

# **12\. Termination**

**Termination by You.**

You may terminate your account and cease using the Services at any time by contacting FieldOps Notes at help@structurely.com.

**Termination by FieldOps Notes.**

FieldOps Notes may suspend or terminate your account or access to the Services at any time, with or without notice, if FieldOps Notes believes (in its sole discretion) that you have violated these Terms, violated applicable law, or pose a risk to the security or integrity of the Services. Upon termination, your right to use the Services will cease immediately.

**Survival.**

Sections 6 (Intellectual Property Rights), 9 (Limitation of Liability), 10 (Disclaimers), 11 (Indemnification), 14 (Governing Law), and 15 (Severability) shall survive any termination or expiration of these Terms.

# **13\. Modification of Terms**

FieldOps Notes may modify these Terms at any time, effective upon posting an updated version on the Services or notifying you by email. Your continued use of the Services after such modifications constitutes your acceptance of the modified Terms. If you do not agree to any modification, you must stop using the Services immediately.

# **14\. Governing Law and Dispute Resolution**

**Governing Law.**

These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflicts of law principles.

**Dispute Resolution.**

Any dispute arising out of or relating to these Terms or the Services shall be resolved through binding arbitration administered by the American Arbitration Association (AAA), in accordance with its rules. The arbitration shall be conducted in Fort Myers, Florida, and you and FieldOps Notes agree to submit to the exclusive jurisdiction of that arbitration forum. EACH PARTY WAIVES THE RIGHT TO JURY TRIAL AND THE RIGHT TO BRING A CLASS ACTION.

**Exception.**

Notwithstanding the foregoing, FieldOps Notes may seek injunctive relief in a court of competent jurisdiction to prevent irreparable harm or to enforce intellectual property rights.

# **15\. Severability**

If any provision of these Terms is found to be invalid, illegal, or unenforceable, that provision shall be modified to the minimum extent necessary to make it valid, or if such modification is not possible, that provision shall be severed from these Terms. The remaining provisions shall remain in full force and effect.

# **16\. Contact Us**

If you have any questions about these Terms, please contact us at:

FieldOps Notes, Inc.

2655 N. Airport Rd.

Fort Myers, FL 33907

help@structurely.com
